import { useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { toast } from "react-toastify"
import { batchesApi, studentsForGenerationApi, studentsByGrApi, suggestApi, CERT_CATEGORIES } from "../../../service/certificate"
import { selectCurrentBranch } from "../../../core/data/redux/slice/branchesSlice"
import { getAllSessions } from "../../../service/sessions"
import { getAllClasses, getAllSections } from "../../../service/classes"
import SavedBatches from "./SavedBatches"
import BatchRosterForm from "./BatchRosterForm"
import BestTeacherForm from "./BestTeacherForm"
import { TYPE_CONFIG } from "./fieldConfigs"

// Types ported to the RMS-parity roster form (buildContext keys verified for
// each). winner (single typed event) + best_teacher (teacher, no GR) are special
// and handled separately.
const PORTED_TYPES = new Set([
  "participation", "appreciation", "general",
  "sports", "competition", "winner", "graduation", "outstanding",
  "cooperative_parents", "attendance", "class_result",
])

// Extract an array from the various shapes our list endpoints return.
const asArray = (x: any): any[] => {
  if (Array.isArray(x)) return x
  if (Array.isArray(x?.sessions)) return x.sessions
  if (Array.isArray(x?.classes)) return x.classes
  if (Array.isArray(x?.sections)) return x.sections
  if (Array.isArray(x?.data)) return x.data
  if (Array.isArray(x?.data?.data)) return x.data.data
  if (Array.isArray(x?.results)) return x.results
  return []
}

// Per-student extra-input columns, keyed by certificate type (the keys
// buildContext reads on the backend).
const EXTRA_COLUMNS: Record<string, { key: string; label: string; type?: string; options?: string[] }[]> = {
  appreciation: [
    { key: "attribution_name", label: "Attribution" },
    { key: "message", label: "Message" },
  ],
  sports: [
    { key: "sport_name", label: "Sport" },
    { key: "position_number", label: "Position #", type: "number" },
  ],
  participation: [
    { key: "competition_names", label: "Competitions (comma-sep)" },
    { key: "position_number", label: "Position #", type: "number" },
  ],
  winner: [
    { key: "event_name", label: "Event" },
    { key: "event_type", label: "Type", type: "select", options: ["competition", "sport"] },
    { key: "position_number", label: "Position #", type: "number" },
  ],
  competition: [
    { key: "competition_name", label: "Competition" },
    { key: "position_number", label: "Position #", type: "number" },
  ],
  attendance: [
    { key: "days_present", label: "Days Present", type: "number" },
    { key: "days_total", label: "Days Total", type: "number" },
  ],
  graduation: [{ key: "message", label: "Message" }],
  outstanding: [
    { key: "message", label: "Message" },
    { key: "event_name", label: "Event (optional)" },
    { key: "event_type", label: "Type", type: "select", options: ["competition", "sport"] },
    { key: "position_number", label: "Position #", type: "number" },
  ],
  cooperative_parents: [{ key: "message", label: "Message" }],
  general: [
    { key: "recipient_name", label: "Recipient (optional)" },
    { key: "title", label: "Title" },
    { key: "custom_text", label: "Custom Text" },
    { key: "message", label: "Message" },
  ],
  best_teacher: [
    { key: "teacher_full_name", label: "Teacher Name" },
    { key: "teacher_designation", label: "Designation" },
    { key: "teacher_subject", label: "Subject/Dept" },
    { key: "message", label: "Message" },
  ],
  class_result: [
    { key: "position_number", label: "Position #", type: "number" },
    { key: "grade_letter", label: "Grade" },
    { key: "percent", label: "Percent %", type: "number" },
  ],
}

// Auto-build the file name uses the type label — look it up in the tree.
const typeLabelOf = (v: string) =>
  CERT_CATEGORIES.flatMap(c => c.types).find(t => t.value === v)?.label || v || "Type"

// Inline chip editor for comma-separated list fields (e.g. competition_names).
// Stores the value as a comma-separated string in the parent's extras state
// so the existing saveBatch→split path works unchanged.
const ChipInput = ({
  value,
  onChange,
  placeholder,
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
}) => {
  const chips = value ? value.split(",").map(s => s.trim()).filter(Boolean) : []
  const [draft, setDraft] = useState("")
  const add = () => {
    const t = draft.trim()
    if (!t) return
    const next = [...chips, t].join(", ")
    onChange(next)
    setDraft("")
  }
  const remove = (i: number) => onChange(chips.filter((_, j) => j !== i).join(", "))
  return (
    <div style={{ minWidth: 180 }}>
      <div className="d-flex flex-wrap gap-1 mb-1">
        {chips.map((c, i) => (
          <span key={i} className="badge bg-primary-transparent text-primary d-inline-flex align-items-center gap-1" style={{ fontSize: 11 }}>
            {c}
            <button
              type="button"
              className="btn-close btn-close-sm"
              style={{ fontSize: 8 }}
              onClick={() => remove(i)}
              aria-label="Remove"
            />
          </span>
        ))}
      </div>
      <div className="input-group input-group-sm">
        <input
          className="form-control form-control-sm"
          placeholder={placeholder || "Add item..."}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add() } }}
        />
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={add} disabled={!draft.trim()}>+</button>
      </div>
    </div>
  )
}

const CertGenerate = () => {
  const currentBranch = useSelector(selectCurrentBranch)
  const [type, setType] = useState("")
  const [activeCat, setActiveCat] = useState(0)
  const [sessions, setSessions] = useState<any[]>([])
  const [sessionId, setSessionId] = useState("")
  const [classes, setClasses] = useState<any[]>([])
  const [classId, setClassId] = useState("")
  const [allSections, setAllSections] = useState<any[]>([])
  const [sectionId, setSectionId] = useState("")
  const [students, setStudents] = useState<any[]>([])
  const [grInput, setGrInput] = useState("")
  const [addingGr, setAddingGr] = useState(false)
  const [topN, setTopN] = useState(3)
  const [suggesting, setSuggesting] = useState(false)
  const [selected, setSelected] = useState<Record<string, boolean>>({})
  const [extras, setExtras] = useState<Record<string, Record<string, string>>>({})
  const [loadingStudents, setLoadingStudents] = useState(false)
  const [fileName, setFileName] = useState("")
  const [fileEdited, setFileEdited] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [refreshSignal, setRefreshSignal] = useState(0)

  const extraCols = useMemo(() => EXTRA_COLUMNS[type] || [], [type])

  const sections = useMemo(() => {
    if (!classId) return allSections
    const f = allSections.filter(
      (s: any) => String(s.class?._id || s.class || s.classId || "") === String(classId)
    )
    return f.length ? f : allSections
  }, [classId, allSections])

  useEffect(() => {
    getAllSessions({ limit: 1000, page: 1 }).then(r => setSessions(asArray(r))).catch(() => {})
    getAllClasses({ limit: 1000, page: 1 } as any).then(r => setClasses(asArray(r))).catch(() => {})
  }, [])

  // Sections vary by session (a class can run different sections year to
  // year), so refetch whenever the picked session changes.
  useEffect(() => {
    if (!sessionId) { setAllSections([]); return }
    getAllSections({ session: sessionId }).then(r => setAllSections(asArray(r))).catch(() => {})
  }, [sessionId])

  // Auto-build the file name (until the user edits it).
  useEffect(() => {
    if (fileEdited) return
    const s = sessions.find(x => x._id === sessionId)
    const sName = (s?.title || s?.name || "Session").replace(/\s+/g, "-")
    const tLabel = typeLabelOf(type).replace(/\s+/g, "-")
    const date = new Date().toISOString().slice(0, 10)
    setFileName(`${sName}_${tLabel}_${date}`)
  }, [type, sessionId, sessions, fileEdited])

  const loadStudents = async () => {
    if (!sessionId) {
      toast.error("Select a session first.")
      return
    }
    setLoadingStudents(true)
    try {
      const res = await studentsForGenerationApi({
        session: sessionId,
        class: classId || undefined,
        section: sectionId || undefined,
      })
      setStudents(res.data || [])
      setSelected({})
      setExtras({})
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load students")
    } finally {
      setLoadingStudents(false)
    }
  }

  // Add specific students by GR number — appends to the roster (and
  // auto-selects them) without clearing what's already loaded.
  const addByGr = async () => {
    const grNumbers = grInput.trim()
    if (!grNumbers) return
    if (!sessionId) {
      toast.error("Select a session first.")
      return
    }
    setAddingGr(true)
    try {
      const res = await studentsByGrApi({ session: sessionId, grNumbers })
      const found = res.data || []
      if (!found.length) {
        toast.error("No students matched those GR numbers in this session.")
        return
      }
      setStudents(prev => {
        const have = new Set(prev.map((s: any) => s.studentId))
        return [...prev, ...found.filter((s: any) => !have.has(s.studentId))]
      })
      setSelected(prev => {
        const next = { ...prev }
        found.forEach((s: any) => (next[s.studentId] = true))
        return next
      })
      toast.success(`Added ${found.length} student(s)`)
      setGrInput("")
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Lookup failed")
    } finally {
      setAddingGr(false)
    }
  }

  // Apply a list of suggested roster rows (top-performers / attendance /
  // teachers) — replaces the roster, pre-fills extras, selects all.
  const applySuggested = (rows: any[], extraKeys: string[], opts: { noStudent?: boolean } = {}) => {
    if (!rows.length) {
      toast.info("No matching records found for this scope.")
      return
    }
    setStudents(
      rows.map(r => ({
        studentId: r.studentId || r.teacherKey,
        name: r.name,
        grNumber: r.grNumber || "",
        className: r.className || "",
        sectionName: r.sectionName || "",
        noStudent: !!opts.noStudent,
      }))
    )
    const sel: Record<string, boolean> = {}
    const ex: Record<string, Record<string, string>> = {}
    rows.forEach(r => {
      const key = r.studentId || r.teacherKey
      sel[key] = true
      ex[key] = {}
      extraKeys.forEach(k => {
        if (r[k] !== undefined && r[k] !== "") ex[key][k] = String(r[k])
      })
    })
    setSelected(sel)
    setExtras(ex)
    toast.success(`Loaded ${rows.length} record(s)`)
  }

  const suggestTop = async () => {
    if (!sessionId) return toast.error("Select a session first.")
    setSuggesting(true)
    try {
      const res = await suggestApi.topPerformers({ session: sessionId, class: classId || undefined, limit: topN })
      applySuggested(res.data || [], ["position_number", "grade_letter", "percent"])
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Suggestion failed")
    } finally {
      setSuggesting(false)
    }
  }

  const loadAttendanceEligible = async () => {
    if (!sessionId) return toast.error("Select a session first.")
    setSuggesting(true)
    try {
      const res = await suggestApi.attendanceEligible({
        session: sessionId,
        class: classId || undefined,
        section: sectionId || undefined,
      })
      applySuggested(res.data || [], ["days_present", "days_total"])
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Lookup failed")
    } finally {
      setSuggesting(false)
    }
  }

  const loadTeachers = async () => {
    setSuggesting(true)
    try {
      const res = await suggestApi.teachers({})
      applySuggested(
        res.data || [],
        [
          "teacher_full_name",
          "teacher_email",
          "teacher_phone",
          "teacher_employee_id",
          "teacher_designation",
          "teacher_title",
          "teacher_affiliation",
          "teacher_subject",
        ],
        { noStudent: true }
      )
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load teachers")
    } finally {
      setSuggesting(false)
    }
  }

  const setExtra = (sid: string, key: string, val: string) =>
    setExtras(prev => ({ ...prev, [sid]: { ...(prev[sid] || {}), [key]: val } }))

  // Load a saved batch back into the build form for editing.
  const onEditBatch = (batch: any) => {
    setType(batch.type)
    const ci = CERT_CATEGORIES.findIndex(c => c.types.some(t => t.value === batch.type))
    if (ci >= 0) setActiveCat(ci)
    setSessionId(batch.session?._id || batch.session || "")
    setClassId(batch.class?._id || batch.class || "")
    setSectionId("")
    setFileName(batch.fileName || "")
    setFileEdited(true)
    const rows: any[] = Array.isArray(batch.studentsData) ? batch.studentsData : []
    setStudents(
      rows.map(r => ({
        studentId: r.student_id,
        name: r.full_name,
        grNumber: r.gr_number,
        className: r.class_name,
        sectionName: r.section_name,
      }))
    )
    const sel: Record<string, boolean> = {}
    const ex: Record<string, Record<string, string>> = {}
    rows.forEach(r => {
      const { student_id, full_name, gr_number, class_name, section_name, ...rest } = r
      if (Array.isArray(rest.competition_names)) rest.competition_names = rest.competition_names.join(", ")
      if (student_id) {
        sel[student_id] = true
        ex[student_id] = Object.fromEntries(
          Object.entries(rest).map(([k, v]) => [k, String(v ?? "")])
        )
      }
    })
    setSelected(sel)
    setExtras(ex)
    setEditingId(batch._id)
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  const cancelEdit = () => {
    setEditingId(null)
    setStudents([])
    setSelected({})
    setExtras({})
    setFileEdited(false)
  }

  const saveBatch = async () => {
    if (!type) return toast.error("Pick a certificate type.")
    if (!sessionId) return toast.error("Pick a session.")
    if (!fileName.trim()) return toast.error("Enter a file name.")
    const chosen = students.filter(s => selected[s.studentId])
    if (!chosen.length) return toast.error("Select at least one student.")

    const rows = chosen.map(s => {
      const ex = extras[s.studentId] || {}
      // Teacher / non-student rows (best-teacher) carry everything in
      // extras and have no student_id.
      const row: any = s.noStudent
        ? { ...ex }
        : {
            student_id: s.studentId,
            full_name: s.name,
            gr_number: s.grNumber,
            class_name: s.className,
            section_name: s.sectionName,
            ...ex,
          }
      if (row.competition_names) {
        row.competition_names = String(row.competition_names)
          .split(",")
          .map((x: string) => x.trim())
          .filter(Boolean)
      }
      return row
    })

    setSaving(true)
    try {
      if (editingId) {
        await batchesApi.update(editingId, {
          fileName: fileName.trim(),
          session: sessionId,
          class: classId || undefined,
          studentsData: rows,
        })
        toast.success("Batch updated")
        setEditingId(null)
      } else {
        const res = await batchesApi.create({
          type,
          fileName: fileName.trim(),
          session: sessionId,
          class: classId || undefined,
          branch: currentBranch?._id || undefined,
          studentsData: rows,
        })
        const ref = res.data?.refNumber
        toast.success(`Batch saved${ref ? ` — ref ${ref}` : ""}`)
      }
      // Reset roster; keep type/session so the saved batch is visible.
      setStudents([])
      setSelected({})
      setExtras({})
      setFileEdited(false)
      setRefreshSignal(n => n + 1)
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Save failed")
    } finally {
      setSaving(false)
    }
  }

  const selectedCount = students.filter(s => selected[s.studentId]).length

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="mb-3">
          <h3 className="page-title mb-1">Generate Certificates</h3>
          <p className="text-muted mb-0">
            Build a roster and <strong>save it as a batch</strong> (it gets a reference number), then
            generate the PDFs from the saved batch below.
          </p>
        </div>

        {/* Build & Save batch */}
        <div className="card">
          <div className="card-header">
            <h5 className="mb-0">1. Build a batch</h5>
          </div>
          <div className="card-body">
            {/* Certificate type — two-tier (category buttons → sub-type pills) */}
            <label className="form-label">Certificate Type *</label>
            <div className="d-flex gap-2 mb-2 flex-wrap">
              {CERT_CATEGORIES.map((cat, i) => (
                <button
                  key={cat.label}
                  type="button"
                  className={`btn btn-sm ${activeCat === i ? "btn-dark" : "btn-outline-secondary"}`}
                  onClick={() => {
                    setActiveCat(i)
                    setType(cat.types[0].value) // jump to first leaf (RMS behavior)
                  }}
                >
                  {cat.label}
                </button>
              ))}
            </div>
            <div className="d-flex gap-2 flex-wrap mb-3">
              {CERT_CATEGORIES[activeCat].types.map(leaf => (
                <button
                  key={leaf.value}
                  type="button"
                  className={`btn btn-sm ${type === leaf.value ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => setType(leaf.value)}
                >
                  {leaf.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {type === "best_teacher" ? (
          <BestTeacherForm onSaved={() => setRefreshSignal(n => n + 1)} />
        ) : PORTED_TYPES.has(type) && TYPE_CONFIG[type] ? (
          <BatchRosterForm
            key={type}
            type={type}
            fields={TYPE_CONFIG[type].fields}
            recipientEditable={TYPE_CONFIG[type].recipientEditable}
            onSaved={() => setRefreshSignal(n => n + 1)}
          />
        ) : (
        <>
          <div className="card mt-3">
            <div className="card-body">
            {/* Scope */}
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label">Session *</label>
                <select className="form-select" value={sessionId} onChange={e => setSessionId(e.target.value)}>
                  <option value="">Select...</option>
                  {sessions.map(s => (
                    <option key={s._id} value={s._id}>{s.title || s.name}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">Class</label>
                <select className="form-select" value={classId} onChange={e => setClassId(e.target.value)}>
                  <option value="">All</option>
                  {classes.map(c => (
                    <option key={c._id} value={c._id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">Section</label>
                <select
                  className="form-select"
                  value={sectionId}
                  onChange={e => setSectionId(e.target.value)}
                  disabled={!classId}
                >
                  <option value="">All</option>
                  {sections.map((s: any) => (
                    <option key={s._id} value={s._id}>{s.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-3 d-flex flex-wrap align-items-center gap-2">
              <button className="btn btn-outline-primary" onClick={loadStudents} disabled={loadingStudents || !type}>
                {loadingStudents ? "Loading..." : "Load Students"}
              </button>
              <span className="text-muted small">or add by GR:</span>
              <input
                className="form-control form-control-sm"
                style={{ maxWidth: 220 }}
                placeholder="e.g. 639010, 639014"
                value={grInput}
                onChange={e => setGrInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && addByGr()}
              />
              <button className="btn btn-sm btn-outline-secondary" onClick={addByGr} disabled={addingGr || !grInput.trim()}>
                {addingGr ? "Adding..." : "Add by GR"}
              </button>
              {!type && <span className="text-muted small ms-2">Pick a type first</span>}

              {/* Data-driven suggestions per merit type */}
              {(type === "class_result" || type === "winner") && (
                <span className="d-inline-flex align-items-center gap-1 ms-2 border-start ps-2">
                  <span className="text-muted small">top</span>
                  <input
                    type="number"
                    min={1}
                    className="form-control form-control-sm"
                    style={{ width: 64 }}
                    value={topN}
                    onChange={e => setTopN(Math.max(1, parseInt(e.target.value) || 1))}
                  />
                  <button className="btn btn-sm btn-outline-success" onClick={suggestTop} disabled={suggesting}>
                    {suggesting ? "..." : "Suggest Top Performers"}
                  </button>
                </span>
              )}
              {type === "attendance" && (
                <button className="btn btn-sm btn-outline-success ms-2" onClick={loadAttendanceEligible} disabled={suggesting}>
                  {suggesting ? "..." : "Load 100% Attendance"}
                </button>
              )}
              {type === "best_teacher" && (
                <button className="btn btn-sm btn-outline-success ms-2" onClick={loadTeachers} disabled={suggesting}>
                  {suggesting ? "..." : "Load Teachers"}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Roster */}
        {students.length > 0 && (
          <div className="card mt-3">
            <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
              <h5 className="mb-0">2. Select students &amp; details ({selectedCount} selected)</h5>
              <div className="d-flex align-items-center gap-2">
                <input
                  className="form-control form-control-sm"
                  style={{ width: 320 }}
                  placeholder="Batch file name"
                  value={fileName}
                  onChange={e => {
                    setFileName(e.target.value)
                    setFileEdited(true)
                  }}
                />
                {editingId && (
                  <button className="btn btn-light" onClick={cancelEdit} disabled={saving}>
                    Cancel
                  </button>
                )}
                <button className="btn btn-primary" onClick={saveBatch} disabled={saving}>
                  {saving ? "Saving..." : (
                    <>
                      <i className="ti ti-device-floppy me-1" />
                      {editingId ? "Update Batch" : "Save Batch"}
                    </>
                  )}
                </button>
              </div>
            </div>
            <div className="card-body table-responsive">
              <table className="table table-bordered align-middle">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={students.length > 0 && students.every(s => selected[s.studentId])}
                        onChange={e => {
                          const all: Record<string, boolean> = {}
                          if (e.target.checked) students.forEach(s => (all[s.studentId] = true))
                          setSelected(all)
                        }}
                      />
                    </th>
                    <th>GR</th>
                    <th>Name</th>
                    <th>Class</th>
                    <th>Section</th>
                    {extraCols.map(c => (
                      <th key={c.key}>{c.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {students.map(s => (
                    <tr key={s.studentId}>
                      <td>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={!!selected[s.studentId]}
                          onChange={e => setSelected(prev => ({ ...prev, [s.studentId]: e.target.checked }))}
                        />
                      </td>
                      <td>{s.grNumber}</td>
                      <td>{s.name}</td>
                      <td>{s.className}</td>
                      <td>{s.sectionName}</td>
                      {extraCols.map(c => (
                        <td key={c.key}>
                          {c.key === "competition_names" ? (
                            <ChipInput
                              value={extras[s.studentId]?.[c.key] || ""}
                              onChange={v => setExtra(s.studentId, c.key, v)}
                              placeholder="Add event..."
                            />
                          ) : c.type === "select" ? (
                            <select
                              className="form-select form-select-sm"
                              value={extras[s.studentId]?.[c.key] || ""}
                              onChange={e => setExtra(s.studentId, c.key, e.target.value)}
                            >
                              <option value="">—</option>
                              {(c.options || []).map(o => (
                                <option key={o} value={o}>{o}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type={c.type === "number" ? "number" : "text"}
                              className="form-control form-control-sm"
                              value={extras[s.studentId]?.[c.key] || ""}
                              onChange={e => setExtra(s.studentId, c.key, e.target.value)}
                            />
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        </>
        )}

        {/* Saved batches */}
        <SavedBatches typeFilter={type} refreshSignal={refreshSignal} onEdit={onEditBatch} />
      </div>
    </div>
  )
}

export default CertGenerate
